import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { CITIES_DATA } from "@/lib/data";

// Build sectors from CITIES_DATA grouped by department
const buildSectors = () => {
  const allCities = Object.values(CITIES_DATA);
  const sec35 = allCities.filter((c) => c.department === "35");
  const sec22 = allCities.filter((c) => c.department === "22");
  const sec56 = allCities.filter((c) => c.department === "56");
  return [
    {
      code: "35",
      name: "Ille-et-Vilaine",
      region: "Bretagne · Côte d'Émeraude",
      team: "Équipe Saint-Malo & Côte d'Émeraude",
      cities: sec35,
      // Communes complémentaires couvertes dans un rayon de 15 km autour de Saint-Malo
      extraCities: [
        "Saint-Suliac",
        "La Gouesnière",
        "Saint-Père-Marc-en-Poulet",
        "Saint-Guinoux",
        "Plerguer",
        "Hirel",
        "Miniac-Morvan",
      ],
      extraTitle: "Également couvertes dans un rayon de 15 km autour de Saint-Malo",
    },
    {
      code: "22",
      name: "Côtes-d'Armor",
      region: "Bretagne · Côte d'Émeraude",
      team: "Équipe Saint-Malo & Côte d'Émeraude",
      cities: sec22,
      // Communes complémentaires couvertes dans un rayon de 20 km autour de Ploubalay (22650)
      extraCities: [
        "Bourseul",
        "Pléven",
        "Saint-Potan",
        "Trédias",
      ],
      extraTitle: "Également couvertes dans un rayon de 20 km autour de Ploubalay (22650)",
    },
    {
      code: "56",
      name: "Morbihan",
      region: "Bretagne sud · Vilaine & Pénerf",
      team: "Équipe Sud Bretagne (Damgan / La Roche-Bernard)",
      cities: sec56,
      // Communes complémentaires couvertes dans un rayon de 15 km autour de La Roche-Bernard
      extraCities: [
        "Billiers",
        "Nivillac",
        "Pénestin",
        "Le Guerno",
        "Noyal-Muzillac",
      ],
      extraTitle: "Également couvertes dans un rayon de 15 km autour de La Roche-Bernard / Damgan",
    },
  ];
};

const Sectors = () => {
  const SECTEURS = buildSectors();

  return (
    <section className="py-24 lg:py-32 bg-white" data-testid="sectors-section">
      <div className="container-tm">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <span className="overline">Nos villes d'intervention</span>
            <h2 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              De la Côte d'Émeraude
              <span className="italic text-gold"> au Morbihan</span>.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="font-sans text-base md:text-lg text-ink/65 leading-relaxed">
              Cliquez sur une ville pour découvrir notre offre dédiée : services, tarifs, simulation de revenus et FAQ locale.
            </p>
          </div>
        </div>

        <div className="space-y-20 lg:space-y-24">
          {SECTEURS.map((sector) => (
            <div key={sector.code} data-testid={`sector-${sector.code}`}>
              <div className="flex items-center gap-4 mb-4">
                <span className="inline-flex items-center justify-center min-w-[64px] h-16 px-5 rounded-2xl bg-sage text-white font-serif text-3xl">
                  {sector.code}
                </span>
                <div>
                  <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight">
                    Secteur {sector.code}, {sector.name}
                  </h3>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-gold font-semibold mt-1">
                    {sector.region}
                  </p>
                </div>
              </div>
              {sector.team && (
                <div className="mb-10 inline-flex items-center gap-2 bg-cream border border-gold/30 rounded-full px-4 py-2" data-testid={`sector-${sector.code}-team-badge`}>
                  <span className="w-2 h-2 rounded-full bg-gold" />
                  <span className="font-sans text-xs text-ink/80">
                    {sector.team}
                  </span>
                </div>
              )}

              {/* Cities with dedicated pages */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 mb-8">
                {sector.cities.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/villes/${c.slug}`}
                    data-testid={`city-card-${c.slug}`}
                    className="group block bg-cream/30 hover:bg-white border border-ink/5 rounded-3xl overflow-hidden transition-all duration-500 hover:shadow-lift"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-cream">
                      <img
                        src={c.heroImage}
                        alt={`${c.name}, Bretagne`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-6 lg:p-7">
                      <div className="flex items-center justify-between mb-3">
                        <span className="overline">Page dédiée</span>
                        <span className="text-xs font-sans text-ink/50">
                          ~{c.intro.avgPrice} / nuit
                        </span>
                      </div>
                      <h4 className="font-serif text-2xl text-ink leading-tight mb-2">
                        Conciergerie Airbnb à <span className="italic text-gold">{c.name}</span>
                      </h4>
                      <p className="font-sans text-sm text-ink/60 leading-relaxed line-clamp-2">
                        {c.intro.text.split(".")[0]}.
                      </p>
                      <span className="mt-4 inline-flex items-center gap-2 text-sage font-sans text-[11px] uppercase tracking-[0.22em] font-semibold group-hover:gap-3 transition-all">
                        Voir la page {c.name}
                        <ArrowRight size={13} strokeWidth={2} />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Extra cities (covered but no dedicated page yet) */}
              {sector.extraCities.length > 0 && (
                <>
                  <p className="overline mb-4">
                    {sector.extraTitle || `Également couvertes dans le secteur ${sector.code}`}
                  </p>
                  <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {sector.extraCities.map((name, i) => (
                      <li
                        key={name}
                        data-testid={`sector-${sector.code}-extra-${i}`}
                        className="bg-cream/40 border border-ink/5 rounded-2xl px-5 py-4 flex items-center gap-3"
                      >
                        <MapPin size={16} strokeWidth={1.8} className="text-gold shrink-0" />
                        <span className="font-sans text-sm text-ink">{name}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Sectors;
